import { registerActions } from "../actionRegistry";
import { api } from "../api";
import { toastSuccess, toastError } from "../ui/messages";
import { cancelnotes } from "./toggles";
import { getDomElement, setText, setHtml, show, hide } from "../utils/dom";
import { escapeHtml } from "../utils/escapeHtml";
import { mountMockupSelectorGrid } from "./components/MockupSelectorGrid";
// Local timer state
let titletimer: ReturnType<typeof setTimeout> | undefined;

/** Criteria the collection wizard filters on. */
export interface WizardCriteria {
  /** "" = all types; "1"/"2" are the wire values. "0" is not valid. */
  artworktype: "" | "1" | "2";
  /** Selected grades. The wire wants a CSV; the conversion happens here, once. */
  pg: number[];
  mg: number[];
  from: string;
  to: string;
}

function toWire(collectionId: string, c: WizardCriteria) {
  return {
    id: collectionId,
    artworktype: c.artworktype,
    pg: c.pg.join(","),
    mg: c.mg.join(","),
    from: c.from,
    to: c.to,
  };
}

/** Above this the server refuses to act and the modal shows a narrow-it-down message. */
export const WIZARD_MAX_MATCHES = 10000;

/**
 * How many artworks match. Returns the count instead of writing `#wiz_count`,
 * `#wiz_preview` and `#wiz_button` — the last of which used to be an HTML string
 * carrying `data-fn="collwizadd"`, the only reason that action existed.
 */
export async function countWizardMatches(
  collectionId: string,
  criteria: WizardCriteria
): Promise<number> {
  const response = await api.collections.wizard(toWire(collectionId, criteria));
  return response.count;
}

/** Add the matching artworks, then land on the collection. */
export async function addWizardMatches(
  collectionId: string,
  criteria: WizardCriteria,
  count: number
): Promise<void> {
  if (!confirm(`Are you sure you want to add ${String(count)} artworks to this collection?`))
    return;
  await api.collections.wizard({ ...toWire(collectionId, criteria), doadd: true });
  location.href = "/collection/" + encodeURIComponent(collectionId);
}

// amCharts v4 is loaded via CDN and has no bundled types

export async function savenotes(id: string): Promise<void> {
  // Read `thenotes-<id>` — the input editnotes() creates. `notes-<id>` is the
  // CONTAINER it gets appended to, and reading that returned the <div>, whose
  // `.value` is undefined. getDomElement casts with `as T`, so it type-checked and
  // posted `undefined ?? ""` — an empty string. That is why this reported "Notes
  // saved" while wiping the note: the write succeeded, with nothing in it.
  const notesEl = getDomElement<HTMLInputElement>("thenotes-" + id);
  // Not in edit mode: bail rather than post a blank over a real note.
  if (!notesEl) return;
  const value = notesEl.value;

  try {
    await api.artists.saveNotes({ id: Number(id), notes: value });
    toastSuccess("Notes saved");
    // `cancelnotes` re-renders the cell from the `oldnotes-<id>` hidden input, so
    // that input is the display source of truth and has to carry what we just
    // stored — otherwise the cell snaps back to the pre-edit text until a reload,
    // which looks exactly like the save having failed.
    const oldEl = getDomElement<HTMLInputElement>("oldnotes-" + id);
    if (oldEl) oldEl.value = value;
    // There used to be a `setHtml("notes-display-" + id, …)` here putting the raw
    // note into innerHTML. Nothing has emitted `notes-display-*` since the 2026-03
    // modal migration, so it was a no-op with a live sink's shape. `cancelnotes`
    // is what actually restores the cell.
    cancelnotes(id);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

interface DashboardArtist {
  id: number;
  name: string | null;
  aiTagged: boolean;
  invisible: boolean;
  welcomeSent: boolean;
  expandedProducts: number;
  bioLive: boolean;
  responsibleId: number | null;
  responsibleName: string | null;
  signedById: number | null;
  signedByName: string | null;
  terminated: boolean;
  notes: string;
  segment: "UNSEGMENTED" | "A" | "B" | "C" | "D" | "FEATURED";
}

function buildDashboardNameHtml(item: DashboardArtist): string {
  // `item.name` is the artist's own display name — attacker-controlled text going
  // into an admin's page. Escaped, like every other interpolation in this family.
  let html = `<div style="width: 280px; overflow: hidden; white-space: nowrap;">${escapeHtml(item.name ?? "")}`;
  if (item.aiTagged) {
    html += `<span style="padding: 0px 0px 0px 5px; color: #999; position: relative; bottom: 2px;"><i class="fas fa-robot"></i></span>`;
  }
  if (item.invisible) {
    html += `<span style="color: red; padding: 0px 0px 0px 5px;"><i class="far fa-eye-slash"></i></span>`;
  }
  if (item.welcomeSent) {
    html += `<span style="color: #aaa; padding: 0px 0px 0px 8px;"><i class="fas fa-envelope"></i></span>`;
  }
  if (item.expandedProducts === 1) {
    html += `<span style="padding: 0px 0px 0px 5px;"><span title="Attending expanded licensing programme with all artworks" class="dashboard-api">EXP ALL</span></span>`;
  } else if (item.expandedProducts === 2) {
    html += `<span style="padding: 0px 0px 0px 5px;"><span title="Attending expanded licensing programme for limited artworks" class="dashboard-api">EXP LIM</span></span>`;
  } else if (item.expandedProducts === 3) {
    html += `<span style="padding: 0px 0px 0px 5px;"><span title="Not attending" class="dashboard-api" style="color: red;">EXP NO</span></span>`;
  }
  html += `</div>`;
  return html;
}

/**
 * Two states, because there are only two facts: the artist has a biography or does not.
 *
 * It used to have four — "AI Biography", "User edited bio", "Has no bio", "Has biography" —
 * which described the retired approval step rather than the profile. Two of them were
 * unreachable: `bioState` is only ever 0 or 1, so the `=== 2` branch never ran.
 *
 * The `hasbio-` marker is what the "Has biography" checkbox above the table filters on
 * (`resellerDashboard.ts`). Nothing had ever emitted it, so ticking the box hid every row.
 */
function buildDashboardBioHtml(item: DashboardArtist): string {
  if (!item.bioLive) {
    return `<span class="dash-bio" style="color:#AD0D0D;"><i class="fas fa-exclamation-triangle"></i> No biography</span>`;
  }
  return (
    `<span class="dash-bio" style="color:#23A631;"><i class="fas fa-check-circle"></i> Has biography</span>` +
    `<input type="hidden" id="hasbio-${item.id}" value="1" />`
  );
}

function buildDashboardRespSignedHtml(item: DashboardArtist): string {
  const naSpan = `<span class="dashboard-na"><i class="far fa-question-circle"></i></span>`;
  // Two branches, only one of which is markup: the placeholder is a literal span,
  // the name is text and must be escaped. Collapsing them into one `??` is what
  // makes this kind of sink easy to get wrong.
  const respName = item.responsibleName == null ? naSpan : escapeHtml(item.responsibleName);
  const signedName = item.signedByName == null ? naSpan : escapeHtml(item.signedByName);
  let html = `${respName}<input type="hidden" id="owner-${item.id}" value="${item.responsibleId ?? ""}" /> / `;
  html += `${signedName}<input type="hidden" id="signedby-${item.id}" value="${item.signedById ?? ""}" />`;
  if (item.terminated) {
    html += `<input type="hidden" id="terminated-${item.id}" value="1" />`;
  }
  return html;
}

function buildDashboardNotesHtml(item: DashboardArtist): string {
  // Notes are typed by an artist and rendered into an admin's dashboard. The
  // attribute below used to escape only `"` while the element body below escaped
  // nothing — partial escaping, which is worse than none because it reads as
  // handled. Both go through `escapeHtml` now.
  const notesContent = item.notes
    ? escapeHtml(item.notes)
    : `<span style="color: #aaa;"><i>No notes added</i></span>`;
  const escapedNotes = escapeHtml(item.notes);
  return (
    `<input type="hidden" id="oldnotes-${item.id}" value="${escapedNotes}" />` +
    `<input type="hidden" id="editnotes-${item.id}" value="0" />` +
    `<div id="notes-${item.id}" style="font-size: 0.8em;">` +
    // data-action/data-fn so actionDelegator dispatches the click; this used to be
    // a jQuery .on("click") re-bound after every dashboard refresh.
    `<div data-editnotes="${item.id}" data-action="call" data-fn="editnotes" data-args="${item.id}">${notesContent}</div></div>`
  );
}

/**
 * Mirror the rendered SUMMARY table (#summary) into the #summary2 slot at the
 * top of the dashboard. The PHP copied the DOM rather than rendering the table
 * twice; kept as-is, but as a registered action instead of an inline <script>.
 */
export function dashboardSummaryMirror(): void {
  const source = getDomElement("summary");
  const target = getDomElement("summary2");
  if (source && target) target.innerHTML = source.innerHTML;
}

export async function refreshartistdashboard(): Promise<void> {
  try {
    const response = await api.artists.dashboard();
    const artists = response.data;
    for (const item of artists) {
      const id = String(item.id);
      if (getDomElement("ad_bio-" + id)) {
        setHtml(`ad_bio-${id}`, buildDashboardBioHtml(item));
      }
      if (getDomElement("ad_name-" + id)) {
        setHtml(`ad_name-${id}`, buildDashboardNameHtml(item));
      }
      if (getDomElement("ad_respsigned-" + id)) {
        setHtml(`ad_respsigned-${id}`, buildDashboardRespSignedHtml(item));
      }
      if (getDomElement("ad_notes-" + id)) {
        setHtml(`ad_notes-${id}`, buildDashboardNotesHtml(item));
      }
      if (getDomElement("ad_segment-" + id)) {
        const segmentHtml =
          item.segment === "UNSEGMENTED"
            ? `<span class="dashboard-na"><i class="far fa-question-circle"></i></span>`
            : item.segment === "FEATURED"
              ? "Featured"
              : item.segment;
        setHtml(`ad_segment-${id}`, segmentHtml);
      }
    }
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function editnotes(id: string): Promise<void> {
  const editNotesEl = getDomElement<HTMLInputElement>("editnotes-" + id);
  if (editNotesEl?.value === "0") {
    try {
      const response = await api.artists.getNotes({ id: Number(id) });
      const notes = response.data;
      const input = document.createElement("input");
      input.type = "text";
      input.id = "thenotes-" + id;
      input.value = notes;
      input.style.cssText =
        "outline: none; width: 470px; border-radius: 7px; border: 1px solid #ccc; padding: 7px 8px 8px 8px;";
      input.addEventListener("keydown", function (e) {
        if (e.key === "Escape") cancelnotes(id);
        if (e.key === "Enter") void savenotes(id);
      });

      const container = getDomElement("notes-" + id);
      if (container) {
        container.innerHTML = "";
        container.appendChild(input);
      }
      input.focus();
    } catch (err: unknown) {
      toastError((err as Error).message);
    }
  }
}

export function edittitle(id: string): void {
  hide(`artworktitle_div_${id}`);
  show(`artworktitle_editor_${id}`);
  getDomElement(`artworktitle_editor_input_${id}`)?.focus();
}

export async function savetitle(id: string): Promise<void> {
  show("artworktitle_div_" + id);
  hide("artworktitle_editor_" + id);

  const inputEl = getDomElement<HTMLInputElement>("artworktitle_editor_input_" + id);
  // A title the user just typed; `setText` cannot be escaped wrong.
  setText("artworktitle_" + id, inputEl?.value ?? "");

  try {
    await api.artwork.saveTitle({ title: inputEl?.value ?? "", id: Number(id) });
    toastSuccess("Title saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientrefresh(): Promise<void> {
  const mockupsEl = getDomElement<HTMLInputElement>("ieclient-selected-mockups");

  try {
    const response = await api.mockups.ieClientRefresh();

    // Update alphastatus text in the generate modal
    const alphaEl = document.getElementById("ieclient-alphastatus");
    if (alphaEl && response.alphastatus) {
      alphaEl.textContent = response.alphastatus;
    }

    // Update icon target — show checkmark when done
    const iconEl = document.getElementById("mockup-icon-target");
    if (iconEl) {
      if (response.stage === 2) {
        iconEl.innerHTML =
          '<i class="fas fa-check-circle" style="font-size: 3em; color: #2EB732;"></i>';
      } else if (response.stage === 1) {
        iconEl.innerHTML =
          '<i class="fas fa-spinner fa-spin" style="font-size: 3em; color: #999;"></i>';
      }
    }

    // Update button target — show download when available
    const btnEl = document.getElementById("mockup-button-target");
    if (btnEl) {
      let btnHtml = "";
      if (response.download) {
        btnHtml += `<a href="${response.download}" data-action="call" data-fn="ieclientstartover2delayed"><span class="button medium green"><i class="fa fa-download"></i> Download mockups</span></a> &nbsp; `;
      }
      // Close is offered whenever the job is no longer running — not just on success.
      // A generation that stalled reports stage 0, and without an exit here the user
      // sits behind "Generating..." with no button at all until they reload the page.
      if (response.stage !== 1) {
        btnHtml += `<span class="button medium grey" data-action="call" data-fn="ieclientstartover2">Close</span>`;
      }
      btnEl.innerHTML = btnHtml;
    }

    if (response.mockups && mockupsEl) {
      mockupsEl.value = response.mockups;
    }

    // Keep the hidden stage input in step with the poll. It is rendered once at SSR
    // (`views/pages/Mockup.tsx`) and nothing else ever updated it, so after a job
    // finished it still read "1" — and `ieclientstartover2` returns early on stage 1,
    // leaving both the Close button and the download link permanently inert until a
    // full page reload. Invisible while nothing ever completed; not any more.
    const stageEl = getDomElement<HTMLInputElement>("ieclient-stage");
    if (stageEl) {
      stageEl.value = String(response.stage);
    }

    if (response.stage === 1) {
      setTimeout(() => {
        void ieclientrefresh();
      }, 1000);
    }
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function getmockups(): Promise<void> {
  await mountMockupSelectorGrid();
}

export function titletimer_fn(): void {
  clearTimeout(titletimer);
  titletimer = setTimeout(function () {
    void titletimerdo();
  }, 2000);
}

export async function titletimerdo(): Promise<void> {
  const titleEl = getDomElement<HTMLInputElement>("ms-title");
  const idEl = getDomElement<HTMLInputElement>("ms-id");

  if (titleEl && titleEl.value !== "") {
    const response = await api.metadata.checkTitle({
      title: titleEl.value,
      id: Number(idEl?.value ?? ""),
    });
    const { count } = response;
    if (count > 0) {
      setHtml(
        "titleinfo",
        `<i class="fas fa-info-circle"></i> &nbsp;${String(count)} artworks with same title`
      );
    } else {
      setHtml(
        "titleinfo",
        '<span style="color: #1BBD22;"><i class="fas fa-check-circle"></i> &nbsp;No artworks with this title</span>'
      );
    }
  }
}

registerActions({
  savenotes,
  dashboardSummaryMirror,
  refreshartistdashboard,
  editnotes,
  ieclientrefresh,
  getmockups,
  titletimer: titletimer_fn,
  titletimerdo,
});
